import { useId, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useModalDialog } from './useModalDialog';
import { cn } from '../lib/cn';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  /** Footer actions, laid out right-aligned (stacked full-width on phones). */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** role="alertdialog" for destructive confirmations. */
  alert?: boolean;
};

/** Centered modal on a native <dialog> (§11b). */
export function Dialog({ open, onClose, title, description, children, footer, size = 'md', alert }: DialogProps) {
  const ref = useModalDialog(open, onClose);
  const titleId = useId();
  const descId = useId();
  return (
    <dialog
      ref={ref}
      role={alert ? 'alertdialog' : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose(); // backdrop click
      }}
      className={cn(
        'wd-dialog m-auto w-[calc(100%-2rem)] rounded-xl border border-border bg-card p-0 text-ink shadow-[0_10px_30px_rgb(20_22_27/0.18)]',
        size === 'sm' && 'max-w-sm',
        size === 'md' && 'max-w-lg',
        size === 'lg' && 'max-w-2xl',
      )}
    >
      {open && (
        <div className="flex max-h-[85dvh] flex-col">
          <header className="flex items-start gap-3 border-b border-divider px-5 py-4">
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-[17px] font-semibold outline-none">
                {title}
              </h2>
              {description && (
                <p id={descId} className="mt-1 text-sm text-muted">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-surface hover:text-ink"
            >
              <X aria-hidden className="size-5" />
            </button>
          </header>
          {children && <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>}
          {footer && (
            <footer className="flex flex-col-reverse gap-2 border-t border-divider px-5 py-4 sm:flex-row sm:justify-end">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}
